import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { UrlParams, PaginatedAthleteBirthdayReportList } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface IUseAthleteBirthdays {
  dateStart?: string;
  dateEnd?: string;
  athleteStatuses?: string[];
  ordering?: string;
  limit?: string;
  enabled?: boolean;
  csvExport?: string;
}

interface QueryKeyType {
  pageParam?: UrlParams;
  queryKey?: (string | undefined | string[])[];
}

function useAthleteBirthdays({
  dateStart,
  dateEnd,
  athleteStatuses,
  ordering,
  limit = '20',
  enabled = true,
  csvExport,
}: IUseAthleteBirthdays): UseInfiniteQueryResult<AxiosResponse<PaginatedAthleteBirthdayReportList>> {
  const fetchAthleteBirthdays = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedAthleteBirthdayReportList>> => {
      const params = pageParam;

      if (dateStart !== undefined) {
        params.start_date = dateStart;
      }
      if (dateEnd !== undefined) {
        params.end_date = dateEnd;
      }
      if (athleteStatuses) {
        params.athlete_statuses = athleteStatuses.join(',');
      }
      if (ordering) {
        params.ordering = ordering.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      }

      return client.get(`reports/athlete-birthdays?${encodeUrlParams(params)}`);
    },
    [ordering, dateStart, dateEnd, athleteStatuses, limit],
  );

  return useInfiniteQuery(
    ['birthdays-list', dateStart, dateEnd, athleteStatuses, ordering, csvExport],
    fetchAthleteBirthdays,
    {
      getNextPageParam: page => {
        return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
      },
      enabled: enabled && dateStart !== undefined && dateEnd !== undefined,
      staleTime: 86400000, // 1 day
    },
  );
}

export default useAthleteBirthdays;
